import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-check-list',
  standalone: true,
  imports: [MatIconModule, MatTooltipModule],
  templateUrl: './check-list.component.html',
  styleUrls: ['./check-list.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckListComponent),
      multi: true,
    },
  ],
})
export class CheckListComponent implements ControlValueAccessor, OnChanges {
  @Input() items: any[] = [];
  @Input() bindLabel = 'label';
  @Input() bindValue = 'value';
  @Input() emptyText = 'No items available';
  @Input() searchPlaceholder = 'Search';
  @Input() ariaLabel = '';

  search = '';
  onlySelected = false;
  disabled = false;
  visibleItems: any[] = [];
  selectedCount = 0;

  private selectedKeys = new Set<string>();
  private onChange: (value: unknown[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.search = '';
      this.onlySelected = false;
      this.updateView();
    }
  }

  isSelected(item: any): boolean {
    return this.selectedKeys.has(this.keyOf(item));
  }

  toggle(item: any): void {
    const key = this.keyOf(item);
    if (this.selectedKeys.has(key)) {
      this.selectedKeys.delete(key);
    } else {
      this.selectedKeys.add(key);
    }
    this.emit();
  }

  toggleOnlySelected(): void {
    this.onlySelected = !this.onlySelected;
    this.updateView();
  }

  selectVisible(): void {
    this.visibleItems.forEach(item => this.selectedKeys.add(this.keyOf(item)));
    this.emit();
  }

  clearVisible(): void {
    this.visibleItems.forEach(item => this.selectedKeys.delete(this.keyOf(item)));
    this.emit();
  }

  onSearch(event: Event): void {
    this.search = (event.target as HTMLInputElement).value;
    this.updateView();
  }

  writeValue(value: unknown): void {
    this.selectedKeys = new Set(Array.isArray(value) ? value.map(String) : []);
    this.updateView();
  }

  registerOnChange(fn: (value: unknown[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private keyOf(item: any): string {
    return String(item[this.bindValue]);
  }

  private updateView(): void {
    const term = this.search.trim().toLowerCase();
    this.selectedCount = this.items.filter(item => this.isSelected(item)).length;
    this.visibleItems = this.items.filter(item =>
      (!this.onlySelected || this.isSelected(item)) &&
      (!term || String(item[this.bindLabel] ?? '').toLowerCase().includes(term)));
  }

  private emit(): void {
    this.updateView();
    this.onChange(this.items.filter(item => this.isSelected(item)).map(item => item[this.bindValue]));
    this.onTouched();
  }
}
